/**
 * Create / edit a Person.
 *
 * Initials and avatar colour auto-derive from the name as you type. The
 * colour pill row lets the user pick a different shade; the initials field
 * is editable in case the auto-derived ones aren't what they want (e.g.
 * "MJ" for "Mary Jo" instead of the default "MJ").
 */

import { useEffect, useState } from 'react';
import {
  useCreatePerson,
  useUpdatePerson,
  colorFromName,
  initialsFromName,
  type PersonDto,
} from '../api/people';
import { useContexts } from '../api/catalog';
import { FormModal } from './FormModal';
import styles from './FormModal.module.css';

interface PersonFormModalProps {
  open: boolean;
  onClose: () => void;
  person?: PersonDto | null;
}

const PALETTE = [
  '#b8714a', '#5a7a4a', '#4a6b8a', '#a8843a', '#8a5a4a',
  '#6a8a5a', '#7a5a8a', '#b8588a', '#a07a3a', '#6a4a6a',
];

export function PersonFormModal({ open, onClose, person }: PersonFormModalProps) {
  const isEdit = !!person;
  const create = useCreatePerson();
  const update = useUpdatePerson();
  const contextsQ = useContexts();
  const contexts = contextsQ.data?.contexts ?? [];

  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [color, setColor] = useState(PALETTE[0]!);
  const [contextId, setContextId] = useState<string | null>(null);
  const [role, setRole] = useState('');
  // Track whether the user manually edited initials/color — if they have,
  // we stop auto-deriving from the name.
  const [initialsTouched, setInitialsTouched] = useState(false);
  const [colorTouched, setColorTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (person) {
      setName(person.name);
      setInitials(person.initials);
      setColor(person.color);
      setContextId(person.contextId);
      setRole(person.role ?? '');
      setInitialsTouched(true);
      setColorTouched(true);
    } else {
      setName('');
      setInitials('');
      setColor(PALETTE[0]!);
      setContextId(null);
      setRole('');
      setInitialsTouched(false);
      setColorTouched(false);
    }
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id]);

  // Auto-derive initials + colour from name while the user hasn't manually
  // edited them.
  useEffect(() => {
    if (!initialsTouched) setInitials(name.trim() ? initialsFromName(name) : '');
    if (!colorTouched) setColor(name.trim() ? colorFromName(name) : PALETTE[0]!);
  }, [name, initialsTouched, colorTouched]);

  const pending = create.isPending || update.isPending;
  const valid = name.trim().length > 0 && initials.trim().length > 0;
  const error =
    (create.error as Error | null)?.message ??
    (update.error as Error | null)?.message ??
    null;

  const submit = () => {
    if (!valid || pending) return;
    const payload = {
      name: name.trim(),
      initials: initials.trim().toUpperCase().slice(0, 4),
      color,
      contextId,
      role: role.trim() || null,
    };
    if (isEdit && person) {
      update.mutate({ id: person.id, input: payload }, { onSuccess: () => onClose() });
    } else {
      create.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit person' : 'New person'}
      subtitle={
        isEdit
          ? undefined
          : 'Add someone you collaborate with. You can link them to projects or areas later.'
      }
      submitLabel={isEdit ? 'Save changes' : 'Create person'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Avatar</span>
        <div className={styles.avatarPreview}>
          <span className={styles.avatarBig} style={{ background: color }}>
            {initials || '?'}
          </span>
          <input
            className={styles.input}
            style={{ width: 80 }}
            value={initials}
            onChange={(e) => {
              setInitials(e.target.value.toUpperCase().slice(0, 4));
              setInitialsTouched(true);
            }}
            placeholder="JC"
            maxLength={4}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="person-name">Name</label>
        <input
          id="person-name"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Marco Vieira"
          maxLength={80}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="person-role">Role (optional)</label>
        <input
          id="person-role"
          className={styles.input}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. Customer Lead, Direct report, Family"
          maxLength={120}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Colour</span>
        <div className={styles.pills}>
          {PALETTE.map((c) => (
            <button
              type="button"
              key={c}
              aria-label={`Pick ${c}`}
              className={`${styles.pill} ${color === c ? styles.pillActive : ''}`}
              onClick={() => {
                setColor(c);
                setColorTouched(true);
              }}
              style={{ paddingLeft: 8, paddingRight: 10 }}
            >
              <span className={styles.pillDot} style={{ background: c }} />
              {color === c ? 'Selected' : ' '}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Context (optional)</span>
        <div className={styles.pills}>
          <button
            type="button"
            className={`${styles.pill} ${contextId === null ? styles.pillActive : ''}`}
            onClick={() => setContextId(null)}
          >
            None
          </button>
          {contexts.map((ctx) => (
            <button
              type="button"
              key={ctx.id}
              className={`${styles.pill} ${contextId === ctx.id ? styles.pillActive : ''}`}
              onClick={() => setContextId(ctx.id)}
            >
              <span className={styles.pillDot} style={{ background: ctx.color }} />
              {ctx.label}
            </button>
          ))}
        </div>
      </div>
    </FormModal>
  );
}
